import React from "react";
import { useThemeContext } from "../../context/themeContext";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { ReactComponent as EmptyIconDark } from "../../assets/IconEmptyDark.svg";
import { ReactComponent as EmptyIconLight } from "../../assets/IconEmptyLight.svg";
import { getContentLabel } from "../../utils/utils";
import { useNavigate } from "react-router-dom";

/** Empty-state panel shown when a user has no collections matching the current view/filters. */
export default function EmptyCollection({
  filtersApplied,
  onAdd,
  contentType,
  view, // "mine" | "saved"
  isOwn,
}) {
  const { mode } = useThemeContext();
  const navigate = useNavigate();

  const cardBg = (theme) =>
    mode === "dark"
      ? theme.palette.background.paper
      : theme.palette.secondary.main;

  const emptyBodyText = filtersApplied ? (
    <Typography textAlign={{ xs: "center", sm: "left" }}>
      No {view === "mine" ? "collections in your library" : "saved collections"}{" "}
      match your current filters, please try again
    </Typography>
  ) : view === "mine" ? (
    <Typography textAlign={{ xs: "center", sm: "left" }}>
      {isOwn ? "You don't" : "This user doesn't"} have any{" "}
      {getContentLabel({
        type: contentType,
        lowercase: true,
        plural: false,
      })}{" "}
      collections yet
    </Typography>
  ) : (
    // view === "saved"
    <Typography textAlign={{ xs: "center", sm: "left" }}>
      You haven't saved any collections yet. Any collections you save on the
      Explore page will appear here
    </Typography>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} display="flex" justifyContent="center">
        <Card sx={{ width: "100%", boxShadow: "none", bgcolor: cardBg }}>
          <CardContent sx={{ pb: { xs: 2, sm: 0 } }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems="center"
              justifyContent="center"
              spacing={{ xs: 0, sm: 2 }}
            >
              <SvgIcon sx={{ width: 100, height: 100 }}>
                {mode === "dark" ? <EmptyIconDark /> : <EmptyIconLight />}
              </SvgIcon>
              {emptyBodyText}
            </Stack>
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end" }}>
            {!filtersApplied && view === "mine" && isOwn && (
              <Button onClick={onAdd}>Create</Button>
            )}
            {view === "saved" && (
              <Button onClick={() => navigate("/explore")}>Explore</Button>
            )}
          </CardActions>
        </Card>
      </Grid>

      {/* Suggestion cards — only shown on the empty "mine" view for the owner */}
      {view === "mine" && isOwn && (
        <>
          <Grid item xs={6} display="flex" justifyContent="center">
            <Card
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                boxShadow: "none",
                bgcolor: cardBg,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                Already created lists elsewhere? Import them now...
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end" }}>
                <Button onClick={() => navigate("/import")}>Import</Button>
              </CardActions>
            </Card>
          </Grid>
          <Grid item xs={6} display="flex" justifyContent="center">
            <Card
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                boxShadow: "none",
                bgcolor: cardBg,
              }}
            >
              <CardContent sx={{ flex: 1 }}>
                Or browse ready-made collections on Explore — save any you like
                and they'll appear here
              </CardContent>
              <CardActions sx={{ justifyContent: "flex-end" }}>
                <Button onClick={() => navigate("/explore")}>Explore</Button>
              </CardActions>
            </Card>
          </Grid>
        </>
      )}
    </Grid>
  );
}
